import React, { useState, useEffect } from 'react';
import {
  Building2, Plus, Edit2, Trash2, Search, Phone, Mail,
  MapPin, Calendar, CreditCard, FileText, Save, X
} from 'lucide-react';

interface Supplier {
  id: number;
  code: string;
  name: string;
  name_kana?: string;
  address?: string;
  phone?: string;
  fax?: string;
  email?: string;
  contact_person?: string;
  payment_terms?: string;
  payment_day?: number;
  bank_name?: string;
  bank_branch?: string;
  account_type?: string;
  account_number?: string;
  account_holder?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const SupplierManagement: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<Supplier>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await fetch('/api/suppliers');
      const data = await response.json();
      if (data.success) {
        setSuppliers(data.data);
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      setError('仕入先の取得に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setFormData(supplier);
    setIsEditing(true);
  };

  const handleCreate = () => {
    // 新規コード生成
    const maxCode = suppliers.reduce((max, s) => {
      const num = parseInt(s.code.replace('SUP', ''));
      return Math.max(max, num);
    }, 0);
    const newCode = `SUP${String(maxCode + 1).padStart(3, '0')}`;

    setFormData({ code: newCode });
    setIsEditing(true);
    setSelectedSupplier(null);
  };

  const handleSave = async () => {
    try {
      const url = selectedSupplier
        ? `/api/suppliers/${selectedSupplier.id}`
        : '/api/suppliers';
      const method = selectedSupplier ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        alert(data.message);
        setIsEditing(false);
        setFormData({});
        setSelectedSupplier(null);
        fetchSuppliers();
      } else {
        throw new Error(data.message);
      }
    } catch (err: any) {
      alert(err.message || '保存に失敗しました');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('この仕入先を削除しますか？')) return;

    try {
      const response = await fetch(`/api/suppliers/${id}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        alert(data.message);
        fetchSuppliers();
      } else {
        throw new Error(data.message);
      }
    } catch (err: any) {
      alert(err.message || '削除に失敗しました');
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Building2 className="w-6 h-6" />
          仕入先管理
        </h1>
      </div>

      {!isEditing ? (
        <>
          {/* 検索・新規作成バー */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="仕入先名またはコードで検索..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={handleCreate}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              新規登録
            </button>
          </div>

          {/* 仕入先リスト */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSuppliers.map(supplier => (
              <div key={supplier.id} className="bg-white rounded-lg shadow-md p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="text-xs text-gray-500 font-mono">{supplier.code}</span>
                    <h3 className="text-lg font-semibold">{supplier.name}</h3>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(supplier)}
                      className="text-blue-500 hover:bg-blue-50 p-1 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(supplier.id)}
                      className="text-red-500 hover:bg-red-50 p-1 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="text-sm text-gray-600 space-y-1">
                  {supplier.contact_person && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">担当:</span>
                      {supplier.contact_person}
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 text-gray-400" />
                      {supplier.phone}
                    </div>
                  )}
                  {supplier.payment_terms && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3 h-3 text-gray-400" />
                      {supplier.payment_terms}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredSuppliers.length === 0 && (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              仕入先が見つかりません
            </div>
          )}
        </>
      ) : (
        /* 編集フォーム */
        <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {selectedSupplier ? '仕入先編集' : '仕入先新規登録'}
            </h2>
            <button
              onClick={() => {
                setIsEditing(false);
                setFormData({});
                setSelectedSupplier(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                仕入先コード *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                disabled={!!selectedSupplier}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                仕入先名 *
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                仕入先名（カナ）
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.name_kana || ''}
                onChange={(e) => setFormData({ ...formData, name_kana: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                担当者名
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.contact_person || ''}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                住所
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                電話番号
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                FAX番号
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.fax || ''}
                onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                メールアドレス
              </label>
              <input
                type="email"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                支払条件
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="例: 月末締め翌月末払い"
                value={formData.payment_terms || ''}
                onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                支払日
              </label>
              <input
                type="number"
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="1-31"
                min="1"
                max="31"
                value={formData.payment_day || ''}
                onChange={(e) => setFormData({ ...formData, payment_day: parseInt(e.target.value) })}
              />
            </div>
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-4">振込先情報</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                銀行名
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.bank_name || ''}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                支店名
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.bank_branch || ''}
                onChange={(e) => setFormData({ ...formData, bank_branch: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                口座種別
              </label>
              <select
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.account_type || ''}
                onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
              >
                <option value="">選択してください</option>
                <option value="普通">普通</option>
                <option value="当座">当座</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                口座番号
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.account_number || ''}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                口座名義
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                value={formData.account_holder || ''}
                onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              備考
            </label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg"
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() => {
                setIsEditing(false);
                setFormData({});
                setSelectedSupplier(null);
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              キャンセル
            </button>
            <button
              onClick={handleSave}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
              disabled={!formData.code || !formData.name}
            >
              <Save className="w-5 h-5" />
              保存
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierManagement;